import { differenceInMonths, differenceInWeeks, differenceInYears, parseISO } from "date-fns";

/**
 * Formats a client's age using the largest unit that still reads as a whole number,
 * so newborns stay legible instead of collapsing to "0".
 *
 * A client of one year or more is shown in years, under a year in months, and under
 * a month in weeks. Every unit counts time actually lived, not calendar boundaries
 * crossed: a client born on the 31st is 0 months old on the 1st.
 *
 * @param birthDate - The client's FHIR `Patient.birthDate` (a `YYYY-MM-DD` string), or undefined.
 * @param today - The date to measure against. Defaults to now; pass it to keep callers testable.
 * @returns The age with its unit (`"3 wk"`, `"5 mo"`, `"36 yr"`), or `""` when there is no birth date.
 */
export function formatClientAge(birthDate: string | undefined, today: Date = new Date()): string {
  if (!birthDate) return "";

  const parsedBirthDate = parseISO(birthDate);

  const years = differenceInYears(today, parsedBirthDate);
  if (years >= 1) {
    return `${years} yr`;
  }

  const months = differenceInMonths(today, parsedBirthDate);
  if (months >= 1) {
    return `${months} mo`;
  }

  return `${differenceInWeeks(today, parsedBirthDate)} wk`;
}
